import { useState } from 'react';

import { Button, Menu, MenuButton, MenuItem, MenuList } from '@chakra-ui/react';

import { AddCircleOutline, IntegrationType } from '@aslanpay/portal-common-lib';

import useFetchIntegrationConfigSpec from 'app/hooks/useFetchIntegrationConfigSpec';

import { AddIntegrationTypeModal } from '../AddIntegrationTypeModal';
import { applicableConfigFields } from '../IntegrationForm';

import { AddTypeMenuProps } from './types';

/** "Add type" menu for one connection. A type with a spec'd field that isn't already set on the connection
 * (e.g. Cezanne's deductionCode for PAYROLL_OUTPUT, the first time it's added) opens a modal to collect it
 * first; a type whose applicable fields are all already set - or that has none, the common case for
 * HRIS_SYNC/TIMESHEET_SYNC - is added directly, in one click, exactly as before. */
function AddTypeMenu({ integration, availableTypes, typeLabels, onAdd }: AddTypeMenuProps) {
  const [pendingType, setPendingType] = useState<IntegrationType | null>(null);
  const { data: specFields = [], isLoading: isSpecLoading } = useFetchIntegrationConfigSpec(
    integration.provider
  );

  const handleSelect = (integrationType: IntegrationType) => {
    const fields = applicableConfigFields(specFields, {
      protocol: integration.protocol,
      integrationTypes: [integrationType],
    });
    const hasUnsetField = fields.some((field) => !integration.config?.[field.key]?.trim());
    if (!hasUnsetField) {
      // Nothing new to collect - add directly, as before. onAdd already surfaces its own error toast, so
      // just swallow the rejection here to avoid an unhandled promise rejection.
      onAdd(integration, integrationType).catch(() => undefined);
      return;
    }
    setPendingType(integrationType);
  };

  return (
    <>
      <Menu>
        <MenuButton
          as={Button}
          size="xs"
          variant="ghost"
          leftIcon={<AddCircleOutline boxSize="14px" />}
          alignSelf="flex-start"
          isDisabled={isSpecLoading}
        >
          Add type
        </MenuButton>
        <MenuList>
          {availableTypes.map((type) => (
            <MenuItem key={type} onClick={() => handleSelect(type)}>
              {typeLabels[type] ?? type}
            </MenuItem>
          ))}
        </MenuList>
      </Menu>
      {pendingType && (
        <AddIntegrationTypeModal
          integration={integration}
          integrationType={pendingType}
          isOpen
          onClose={() => setPendingType(null)}
          onConfirm={(config) => onAdd(integration, pendingType, config)}
        />
      )}
    </>
  );
}

export default AddTypeMenu;
